import React from 'react';
import { Clock, Plus, Trash2 } from 'lucide-react';

// Reusable Schedule Form Component
export const ScheduleTimingForm = ({
    frequency,
    setFrequency,
    quietHoursEnabled,
    setQuietHoursEnabled,
    slots,
    addSlot,
    removeSlot,
    updateSlot,
    isGlobal = false,
    useGlobalQuietHours,
    setUseGlobalQuietHours
}) => {
    return (
        <div className="space-y-6">
            <div>
                <label htmlFor={`notif-frequency-${isGlobal ? 'global' : 'filter'}`} className="block text-sm font-semibold text-gray-900 mb-2">Notification Frequency</label>
                <div className="relative">
                    <select
                        id={`notif-frequency-${isGlobal ? 'global' : 'filter'}`}
                        className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                        value={frequency}
                        onChange={(e) => setFrequency(e.target.value)}
                    >
                        {!isGlobal && <option value="inherit">Same as Global</option>}
                        <option value="instant">Instantly (approx. 1 min)</option>
                        <option value="10m">Every 10 minutes</option>
                        <option value="30m">Every 30 minutes</option>
                        <option value="1h">Once per hour</option>
                        <option value="24h">Once a day</option>
                    </select>
                    <svg className="w-5 h-5 text-gray-400 absolute right-3 top-3 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>

            {/* Quiet Hours Wrapper */}
            <div className="space-y-4">
                {/* For per-filter: Option to use global or custom */}
                {!isGlobal && (
                    <div className="flex items-center gap-3 pb-2 border-b border-gray-100">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="qh-mode"
                                checked={useGlobalQuietHours}
                                onChange={() => setUseGlobalQuietHours(true)}
                                className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Use Global Quiet Hours</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="qh-mode"
                                checked={!useGlobalQuietHours}
                                onChange={() => setUseGlobalQuietHours(false)}
                                className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Custom Schedule</span>
                        </label>
                    </div>
                )}

                {/* Actual Quiet Hours Controls */}
                {(isGlobal || !useGlobalQuietHours) && (
                    <>
                        <div className="flex items-start gap-3">
                            <input
                                type="checkbox"
                                id={`quiet-hours-${isGlobal ? 'global' : 'filter'}`}
                                aria-label="Enable quiet hours"
                                checked={quietHoursEnabled}
                                onChange={(e) => setQuietHoursEnabled(e.target.checked)}
                                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                            />
                            <label htmlFor={`quiet-hours-${isGlobal ? 'global' : 'filter'}`} className="cursor-pointer">
                                <span className="block text-sm font-semibold text-gray-900">Enable quiet hours</span>
                                <span className="block text-sm text-gray-500">Stop receiving notifications during specific hours</span>
                            </label>
                        </div>

                        {quietHoursEnabled && (
                            <div className="pl-7 space-y-4">
                                {slots.map((slot) => (
                                    <div key={slot.id} className="relative">
                                        <div className="grid grid-cols-2 gap-6">
                                            <div>
                                                <label htmlFor={`slot-from-${slot.id}`} className="block text-sm font-medium text-gray-700 mb-1">From <span className="text-gray-400 font-normal">(hours:minutes)</span></label>
                                                <div className="relative">
                                                    <input
                                                        id={`slot-from-${slot.id}`}
                                                        type="time"
                                                        aria-label="Quiet hours start time"
                                                        className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        value={slot.from}
                                                        onChange={(e) => updateSlot(slot.id, 'from', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label htmlFor={`slot-to-${slot.id}`} className="block text-sm font-medium text-gray-700 mb-1">To <span className="text-gray-400 font-normal">(hours:minutes)</span></label>
                                                <div className="relative">
                                                    <input
                                                        id={`slot-to-${slot.id}`}
                                                        type="time"
                                                        aria-label="Quiet hours end time"
                                                        className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        value={slot.to}
                                                        onChange={(e) => updateSlot(slot.id, 'to', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {slots.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeSlot(slot.id)}
                                                className="absolute -right-3 -top-2 p-1.5 bg-red-50 hover:bg-red-100 text-red-500 rounded-full border border-red-100 transition-colors shadow-sm"
                                                title="Remove this time slot"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    onClick={addSlot}
                                    className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors pt-1"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add another time slot
                                </button>

                                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3 text-blue-900 mt-2">
                                    <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm">
                                        <p>
                                            Your timezone: <span className="font-semibold">{Intl.DateTimeFormat().resolvedOptions().timeZone}</span>
                                            <button type="button" className="ml-2 text-blue-600 hover:text-blue-800 underline">Change timezone ↗</button>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
